/**
 * React Error Boundary components for graceful error handling
 */
import React from 'react';
import errorHandler from '../services/errorHandler';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    this.setState({
      error,
      errorInfo,
    });

    // Log error through our centralized error handler
    errorHandler.logError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'Unknown',
      props: this.props.logProps ? this.props : undefined,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      // Default fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          showDetails={this.props.showDetails}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, onRetry, showDetails = false }) => {
  return (
    <div className='error-boundary'>
      <div className='error-boundary-content'>
        <div className='error-icon'>⚠️</div>
        <h2>Something went wrong</h2>
        <p>We&apos;re sorry, but something unexpected happened.</p>

        {showDetails && error && (
          <details className='error-details'>
            <summary>Error Details</summary>
            <pre>{error.toString()}</pre>
          </details>
        )}

        <div className='error-actions'>
          <button onClick={onRetry} className='retry-button'>
            Try Again
          </button>
          <button onClick={() => window.location.reload()} className='reload-button'>
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
};

// Specialized error boundaries for different sections
export const PageErrorBoundary = ({ children, pageName }) => (
  <ErrorBoundary
    name={`Page-${pageName}`}
    fallback={(error, retry) => (
      <div className='page-error'>
        <h1>Page Error</h1>
        <p>This page encountered an error and couldn&apos;t load properly.</p>
        <button onClick={retry}>Try Again</button>
        <button onClick={() => window.history.back()}>Go Back</button>
      </div>
    )}
  >
    {children}
  </ErrorBoundary>
);

export const ComponentErrorBoundary = ({ children, componentName }) => (
  <ErrorBoundary
    name={`Component-${componentName}`}
    fallback={(error, retry) => (
      <div className='component-error'>
        <p>
          This component couldn&apos;t load. <button onClick={retry}>Try again</button>
        </p>
      </div>
    )}
  >
    {children}
  </ErrorBoundary>
);

export const AsyncErrorBoundary = ({ children }) => (
  <ErrorBoundary
    name='Async-Component'
    fallback={(error, retry) => (
      <div className='async-error'>
        <div className='loading-spinner'></div>
        <p>
          Loading failed. <button onClick={retry}>Retry</button>
        </p>
      </div>
    )}
  >
    {children}
  </ErrorBoundary>
);

export default ErrorBoundary;
